import { useRef, useState } from "react";
import { keyframes, styled } from "styled-components";
import RedirectComponent, { RedirectText } from "../../RedirectComponent";
import { UseCase } from "../../base/UseCase";
import { Check } from "@cleverbase-trust-drivers/shared/images/CheckMark";
import confirmationAuth from "../../../images/confirmation_auth.svg";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const ConfirmText = styled.p`
  text-align: center;
  font-size: 1.6rem;
  line-height: 2.2rem;
  font-weight: 400;
  color: #23231f;
  margin: 0;
  white-space: pre-line;
`;

const HiddenRedirect = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const ConfirmationImage = styled.img`
  width: 200px;
  height: 200px;
  display: block;
`;

const ContinueButton = styled.button`
  display: flex;
  border: 1px solid #23231f;
  padding: 16px 12px 16px 16px;
  height: 56px;
  background: transparent;
  border-radius: 4px;
  color: #23231f;
  font-size: 18px;
  font-weight: 500;
  line-height: 22px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  width: fit-content;

  @media (hover: hover) {
    &:hover {
      background: #163563;
      color: #f7f7f6;
      border-color: transparent;
    }
  }

  &:focus-visible {
    outline: none;
    background: #163563;
    color: #f7f7f6;
    border-color: transparent;
    box-shadow:
      0 0 0 2px #f7f7f6,
      0 0 0 4px #126de3;
  }

  &:disabled {
    cursor: default;
  }
`;

const ChevronIcon = styled.span`
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

/* Takes the chevron's place once tapped, so the button keeps its width. */
const Spinner = styled.span`
  width: 24px;
  height: 24px;
  box-sizing: border-box;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 2.4s;
  }
`;

export interface ConfirmingProps {
  redirectToUri?: string | undefined;
  redirectAfter?: number | undefined;
  clientName?: string | undefined;
  showNonPKIoAlert: boolean;
  useCase: UseCase;
  t: {
    redirect: RedirectText;
  };
  isDesktop: boolean;
}

export default function Confirming({
  redirectToUri,
  redirectAfter,
  clientName,
  useCase,
  t,
  isDesktop,
}: ConfirmingProps): JSX.Element {
  const [hasContinued, setHasContinued] = useState(false);
  // The disabled attribute is what actually stops the second tap: React does
  // not dispatch click handlers on disabled form controls. This ref guards the
  // navigation itself, so the call site stays safe if the button is ever
  // restyled into something that is not a <button> and loses that behaviour.
  const continuing = useRef(false);

  function continueToClient(uri: string): void {
    if (continuing.current) return;
    continuing.current = true;
    setHasContinued(true);
    window.location.replace(uri);
  }

  if (useCase === "Identification") {
    return (
      <Container data-testid="authentication_confirming">
        <Check data-testid="consent_checkmark" variant="identification" />
      </Container>
    );
  } else {
    return (
      <Container data-testid="authentication_confirming">
        {isDesktop && !!redirectToUri && (
          <HiddenRedirect>
            <RedirectComponent
              seconds={redirectAfter}
              redirectToUri={redirectToUri}
              t={t.redirect}
              clientName={clientName}
            />
          </HiddenRedirect>
        )}
        <ConfirmText>{t.redirect.redirectingText}</ConfirmText>
        <ConfirmationImage src={confirmationAuth} alt="" />
        {!isDesktop && !!redirectToUri && (
          <ContinueButton
            type="button"
            data-testid="authentication_continue"
            disabled={hasContinued}
            aria-busy={hasContinued}
            onClick={() => continueToClient(redirectToUri)}
          >
            {t.redirect.continueButtonText}
            {hasContinued ? (
              <Spinner
                data-testid="authentication_continue_spinner"
                aria-hidden="true"
              />
            ) : (
              <ChevronIcon aria-hidden="true">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="24px"
                  viewBox="0 -960 960 960"
                  width="24px"
                  fill="currentColor"
                >
                  <path d="M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z" />
                </svg>
              </ChevronIcon>
            )}
          </ContinueButton>
        )}
      </Container>
    );
  }
}
